import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  useTheme
} from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  FiZap,
  FiTarget,
  FiCompass,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiFilter,
  FiAward
} from "react-icons/fi";
import { isStatusRow } from "../../context/MarketOverviewContext";
import { Chip } from "../../components/SharedPageStyles";

const ASSET_NAMES = {
  "XAUUSD": "Gold Spot",
  "XAGUSD": "Silver Spot",
  "USOIL": "WTI Crude Oil",
  "S&P500": "S&P 500",
  "NASDAQ": "Nasdaq 100",
  "RUSSELL 2000": "Russell 2000",
  "USDX": "US Dollar Index",
  "EURUSD": "EUR / USD",
  "USDJPY": "USD / JPY",
  "USDCHF": "USD / CHF",
  "GBPUSD": "GBP / USD",
  "USDCAD": "USD / CAD",
  "AUDUSD": "AUD / USD",
  "BTCUSD": "Bitcoin",
  "ETHUSD": "Ethereum",
  "EURX": "Euro Currency Index",
  "JPYX": "Yen Currency Index"
};

const HORIZONS = [
  {
    id: "short",
    label: "Short Term",
    sublabel: "M15 – H1",
    icon: <FiZap />,
    accent: "#38bdf8"
  },
  {
    id: "middle",
    label: "Middle Term",
    sublabel: "H1 – D1",
    icon: <FiTarget />,
    accent: "#818cf8"
  },
  {
    id: "long",
    label: "Long Term",
    sublabel: "D1 – W1",
    icon: <FiCompass />,
    accent: "#c084fc"
  }
];

const CATEGORIES = ["All", "Forex", "Indices", "Commodities", "Crypto"];

export const AssetRankingsModal = ({ open, onClose, rows = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [activeHorizon, setActiveHorizon] = useState("middle");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Extract unique status rows
  const statusRows = useMemo(() => {
    const valid = rows.filter(isStatusRow);
    const map = new Map();
    valid.forEach(r => {
      const cat = (r.catalyst || r.Catalyst || "").toUpperCase().trim();
      if (cat && !map.has(cat)) {
        map.set(cat, r);
      }
    });
    return Array.from(map.values());
  }, [rows]);

  // Process and rank assets for current horizon (Best score first)
  const rankedAssets = useMemo(() => {
    const list = statusRows.map(row => {
      const cat = (row.catalyst || row.Catalyst || "").toUpperCase().trim();
      const name = ASSET_NAMES[cat] || row.name || cat;
      const category = row.category || row.Category || "Other";

      let score = 0;
      let status = "Neutral";

      if (activeHorizon === "short") {
        score = row.score_short_term ?? row.advisor_scores?.short_term ?? 0;
        status = row.status_short_term || (score > 0 ? "Bullish" : score < 0 ? "Bearish" : "Neutral");
      } else if (activeHorizon === "middle") {
        score = row.score_middle_term ?? row.advisor_scores?.middle_term ?? 0;
        status = row.status_middle_term || (score > 0 ? "Bullish" : score < 0 ? "Bearish" : "Neutral");
      } else {
        score = row.score_long_term ?? row.advisor_scores?.long_term ?? 0;
        status = row.status_long_term || (score > 0 ? "Bullish" : score < 0 ? "Bearish" : "Neutral");
      }

      // Ensure English
      if (status.toLowerCase().includes("bull") || status.toLowerCase().includes("hauss")) {
        status = "Bullish";
      } else if (status.toLowerCase().includes("bear") || status.toLowerCase().includes("baiss")) {
        status = "Bearish";
      } else {
        status = "Neutral";
      }

      const oppScore = row.opportunity_score != null ? Number(row.opportunity_score) : null;
      const grade = row.grade || null;

      // Medals apply STRICTLY to the asset's corresponding optimal trading horizon
      const activeTf = row.optimal_tf;
      const isOptimalHorizon =
        (activeHorizon === "short" && (activeTf === "M15-H1" || row.horizon === "Short Term")) ||
        (activeHorizon === "middle" && (activeTf === "H1-D1" || row.horizon === "Middle Term")) ||
        (activeHorizon === "long" && (activeTf === "D1-W1" || row.horizon === "Long Term"));

      const medalForHorizon = isOptimalHorizon ? (row.medal || null) : null;

      return {
        ...row,
        cleanCat: cat,
        cleanName: name,
        cleanCategory: category,
        score,
        absScore: Math.abs(score),
        status,
        oppScore,
        grade,
        isOptimalHorizon,
        medal: medalForHorizon
      };
    });

    // Filter: Show assets with an active score on this horizon OR an active qualified setup (Grade B/A/A+ >= 50)
    let filtered = list.filter(item => item.score !== 0 || (item.oppScore != null && item.oppScore >= 50));

    // Apply category filter
    if (selectedCategory !== "All") {
      filtered = filtered.filter(
        item => item.cleanCategory.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Sort logic:
    // 1. Assets that earned a podium medal ON THIS HORIZON (a.medal !== null) are placed at the top!
    //    (e.g. on Short Term, USDX 🥉 sits at #1. On Long Term, Gold 🥇 and BTC 🥈 sit at #1 and #2).
    // 2. Next, other active opportunities are ranked by Opportunity Score (Grade A+ > A > B > C).
    // 3. Ties broken by absolute score intensity on current horizon (|score|).
    // 4. Directional score (Bullish > Bearish).
    filtered.sort((a, b) => {
      const aHasMedal = Boolean(a.medal);
      const bHasMedal = Boolean(b.medal);
      if (bHasMedal !== aHasMedal) {
        return (bHasMedal ? 1 : 0) - (aHasMedal ? 1 : 0);
      }
      const aOpp = a.oppScore ?? -1;
      const bOpp = b.oppScore ?? -1;
      if (bOpp !== aOpp) {
        return bOpp - aOpp;
      }
      if (b.absScore !== a.absScore) {
        return b.absScore - a.absScore;
      }
      return b.score - a.score;
    });

    return filtered;
  }, [statusRows, activeHorizon, selectedCategory]);


  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? "#0d111a" : "#ffffff",
          color: isDark ? "#e2e8f0" : "#0f172a",
          borderRadius: "16px",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(15, 23, 42, 0.12)",
          boxShadow: isDark
            ? "0 26px 65px rgba(0, 0, 0, 0.88), 0 0 0 1px rgba(255, 255, 255, 0.06)"
            : "0 22px 48px rgba(0, 0, 0, 0.12)",
          overflow: "hidden",
          maxHeight: "85vh",
          maxWidth: "680px",
          width: "100%"
        }
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "14px 22px",
          borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
          background: isDark
            ? "linear-gradient(180deg, rgba(22, 27, 42, 0.95) 0%, rgba(13, 17, 26, 0.95) 100%)"
            : "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.9) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "11px",
              background: isDark
                ? "linear-gradient(135deg, rgba(128, 203, 196, 0.16) 0%, rgba(15, 118, 110, 0.08) 100%)"
                : "linear-gradient(135deg, rgba(15, 118, 110, 0.14) 0%, rgba(128, 203, 196, 0.06) 100%)",
              border: isDark ? "1px solid rgba(128, 203, 196, 0.4)" : "1px solid rgba(15, 118, 110, 0.3)",
              boxShadow: isDark ? "0 0 14px rgba(128, 203, 196, 0.20)" : "0 0 10px rgba(15, 118, 110, 0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              color: isDark ? "#80cbc4" : "#0b3b35"
            }}
          >
            <FiAward />
          </div>
          <div>
            <Chip style={{ fontSize: "13px", padding: "5px 14px" }}>Asset Ranking</Chip>
          </div>
        </div>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            padding: "6px",
            color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
            "&:hover": {
              color: isDark ? "#ffffff" : "#000000",
              bgcolor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"
            }
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </div>

      {/* Horizon Tabs (Short, Middle, Long - Without Descriptions) */}
      <div
        style={{
          padding: "12px 20px 10px 20px",
          background: isDark ? "rgba(18, 22, 34, 0.6)" : "rgba(248, 250, 252, 0.8)",
          borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid rgba(0, 0, 0, 0.06)"
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "10px"
          }}
        >
          {HORIZONS.map(h => {
            const active = activeHorizon === h.id;
            return (
              <button
                key={h.id}
                onClick={() => setActiveHorizon(h.id)}
                style={{
                  padding: "9px 12px",
                  borderRadius: "9px",
                  border: active
                    ? `1px solid ${h.accent}`
                    : isDark
                      ? "1px solid rgba(255, 255, 255, 0.08)"
                      : "1px solid rgba(15, 23, 42, 0.08)",
                  background: active
                    ? isDark
                      ? `linear-gradient(135deg, ${h.accent}22 0%, rgba(18, 22, 34, 0.9) 100%)`
                      : `linear-gradient(135deg, ${h.accent}18 0%, #ffffff 100%)`
                    : isDark
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(255, 255, 255, 0.6)",
                  boxShadow: active ? `0 0 14px ${h.accent}24` : "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ color: active ? h.accent : isDark ? "#94a3b8" : "#64748b", fontSize: "16px", display: "inline-flex" }}>
                    {h.icon}
                  </span>
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: active ? (isDark ? "#ffffff" : "#0f172a") : isDark ? "#cbd5e1" : "#475569"
                    }}
                  >
                    {h.label}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "10.5px",
                    fontWeight: 600,
                    padding: "2px 7px",
                    borderRadius: "5px",
                    background: active ? `${h.accent}25` : isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                    color: active ? h.accent : isDark ? "#64748b" : "#94a3b8"
                  }}
                >
                  {h.sublabel}
                </span>
              </button>
            );
          })}
        </div>

        {/* Category Filter Chips */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "10px",
            marginTop: "10px",
            paddingTop: "10px",
            borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "12px", color: isDark ? "#64748b" : "#94a3b8", marginRight: "4px" }}>
              <FiFilter style={{ verticalAlign: "middle" }} />
            </span>
            {CATEGORIES.map(cat => {
              const isSelected = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    padding: "4px 11px",
                    borderRadius: "6px",
                    border: isSelected
                      ? "1px solid rgba(99, 102, 241, 0.6)"
                      : isDark
                        ? "1px solid rgba(255, 255, 255, 0.08)"
                        : "1px solid rgba(0, 0, 0, 0.08)",
                    background: isSelected
                      ? "rgba(99, 102, 241, 0.18)"
                      : isDark
                        ? "rgba(255, 255, 255, 0.03)"
                        : "rgba(0, 0, 0, 0.03)",
                    color: isSelected ? "#818cf8" : isDark ? "#94a3b8" : "#64748b",
                    fontSize: "12px",
                    fontWeight: isSelected ? 600 : 500,
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: "12px", color: isDark ? "#64748b" : "#94a3b8", fontWeight: 500 }}>
            {rankedAssets.length} assets
          </div>
        </div>
      </div>

      {/* Content list */}
      <DialogContent
        sx={{
          padding: "12px 20px 16px 20px",
          overflowY: "auto",
          maxHeight: "58vh",
          "&::-webkit-scrollbar": {
            width: "5px",
            height: "5px"
          },
          "&::-webkit-scrollbar-track": {
            background: isDark ? "rgba(0, 0, 0, 0.22)" : "rgba(0, 0, 0, 0.05)",
            borderRadius: "999px",
            border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.05)"
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDark
              ? "linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44))"
              : "linear-gradient(180deg, rgba(15, 118, 110, 0.35), rgba(15, 118, 110, 0.55))",
            borderRadius: "999px",
            border: isDark ? "1px solid rgba(128, 203, 196, 0.20)" : "1px solid rgba(15, 118, 110, 0.20)"
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: isDark
              ? "linear-gradient(180deg, rgba(128, 203, 196, 0.45), rgba(128, 203, 196, 0.65))"
              : "linear-gradient(180deg, rgba(15, 118, 110, 0.5), rgba(15, 118, 110, 0.75))"
          }
        }}
      >
        {rankedAssets.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "45px 16px",
              color: isDark ? "#64748b" : "#94a3b8",
              fontSize: "13px"
            }}
          >
            No active opportunities for this horizon and category.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
            {rankedAssets.map((asset, idx) => {
              const rank = idx + 1;
              const medalEmoji = asset.medal || null;
              const isGold = medalEmoji === "🥇";
              const isSilver = medalEmoji === "🥈";
              const isBronze = medalEmoji === "🥉";
              const isHonorable = medalEmoji === "🎖️";

              const isBullish = asset.score > 0;
              const isBearish = asset.score < 0;

              const scoreColor = isBullish
                ? "#10b981"
                : isBearish
                  ? "#ef4444"
                  : isDark
                    ? "#94a3b8"
                    : "#64748b";

              const scoreBg = isBullish
                ? "rgba(16, 185, 129, 0.12)"
                : isBearish
                  ? "rgba(239, 68, 68, 0.12)"
                  : isDark
                    ? "rgba(148, 163, 184, 0.1)"
                    : "rgba(100, 116, 139, 0.08)";

              const gradeColor =
                asset.grade === "A+"
                  ? "#10b981"
                  : asset.grade === "A"
                    ? "#38bdf8"
                    : asset.grade === "B"
                      ? "#f59e0b"
                      : "#94a3b8";

              return (
                <div
                  key={asset.cleanCat}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 14px",
                    borderRadius: "11px",
                    background: isDark
                      ? isGold
                        ? "linear-gradient(90deg, rgba(212, 175, 55, 0.12) 0%, rgba(20, 24, 36, 0.8) 100%)"
                        : isSilver
                          ? "linear-gradient(90deg, rgba(148, 163, 184, 0.10) 0%, rgba(20, 24, 36, 0.8) 100%)"
                          : isBronze
                            ? "linear-gradient(90deg, rgba(180, 120, 75, 0.10) 0%, rgba(20, 24, 36, 0.8) 100%)"
                            : isHonorable
                              ? "linear-gradient(90deg, rgba(99, 102, 241, 0.10) 0%, rgba(20, 24, 36, 0.8) 100%)"
                              : "rgba(20, 24, 36, 0.6)"
                      : "#f8fafc",
                    border: isGold
                      ? "1px solid rgba(212, 175, 55, 0.35)"
                      : isSilver
                        ? "1px solid rgba(148, 163, 184, 0.3)"
                        : isBronze
                          ? "1px solid rgba(180, 120, 75, 0.3)"
                          : isHonorable
                            ? "1px solid rgba(99, 102, 241, 0.3)"
                            : isDark
                              ? "1px solid rgba(255, 255, 255, 0.06)"
                              : "1px solid rgba(0, 0, 0, 0.06)",
                    boxShadow: isGold
                      ? "0 2px 10px rgba(212, 175, 55, 0.10)"
                      : isSilver
                        ? "0 2px 8px rgba(148, 163, 184, 0.08)"
                        : isBronze
                          ? "0 2px 8px rgba(180, 120, 75, 0.08)"
                          : isHonorable
                            ? "0 2px 8px rgba(99, 102, 241, 0.08)"
                            : "none",
                    transition: "all 0.15s ease"
                  }}
                >
                  {/* Left: Rank + Asset Symbol & Name */}
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: "210px" }}>
                    {/* Rank badge */}
                    <div
                      title={medalEmoji ? `Podium ${medalEmoji} (Optimal Trading Horizon)` : `Rank #${rank}`}
                      style={{
                        width: "33px",
                        height: "33px",
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: medalEmoji ? "16px" : "12px",
                        background: isGold
                          ? "rgba(212, 175, 55, 0.2)"
                          : isSilver
                            ? "rgba(148, 163, 184, 0.2)"
                            : isBronze
                              ? "rgba(180, 120, 75, 0.2)"
                              : isHonorable
                                ? "rgba(99, 102, 241, 0.2)"
                                : isDark
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.05)",
                        color: isGold
                          ? "#ffd700"
                          : isSilver
                            ? "#cbd5e1"
                            : isBronze
                              ? "#d97706"
                              : isHonorable
                                ? "#818cf8"
                                : isDark
                                  ? "#64748b"
                                  : "#94a3b8",
                        border: isGold
                          ? "1px solid rgba(212, 175, 55, 0.4)"
                          : isSilver
                            ? "1px solid rgba(148, 163, 184, 0.35)"
                            : isBronze
                              ? "1px solid rgba(180, 120, 75, 0.35)"
                              : isHonorable
                                ? "1px solid rgba(99, 102, 241, 0.35)"
                                : "none",
                        flexShrink: 0
                      }}
                    >
                      {medalEmoji || `#${rank}`}
                    </div>

                    {/* Asset details */}
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                        <span
                          style={{
                            fontFamily: "'JetBrains Mono', Consolas, monospace",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: isDark ? "#f8fafc" : "#0f172a"
                          }}
                        >
                          {asset.cleanCat}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 600,
                            padding: "1px 6px",
                            borderRadius: "4px",
                            background: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)",
                            color: isDark ? "#94a3b8" : "#64748b"
                          }}
                        >
                          {asset.cleanCategory}
                        </span>
                      </div>
                      <div style={{ fontSize: "12px", color: isDark ? "#94a3b8" : "#64748b", marginTop: "1px" }}>
                        {asset.cleanName}
                      </div>
                    </div>
                  </div>

                  {/* Middle: Direction Icon + Score */}
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: "95px" }}>
                    <span
                      title={asset.status}
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        padding: "4px 7px",
                        borderRadius: "7px",
                        background: scoreBg,
                        color: scoreColor,
                        border: `1px solid ${scoreColor}33`,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {isBullish ? <FiTrendingUp /> : isBearish ? <FiTrendingDown /> : <FiMinus />}
                    </span>

                    <div style={{ textAlign: "right", minWidth: "35px" }}>
                      <div
                        style={{
                          fontFamily: "'JetBrains Mono', Consolas, monospace",
                          fontSize: "14px",
                          fontWeight: 700,
                          color: scoreColor
                        }}
                      >
                        {asset.score > 0 ? `+${asset.score}` : asset.score}
                      </div>
                    </div>
                  </div>

                  {/* Right: Trade Advisor Grade + Opportunity Score on 10 */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", minWidth: "98px" }}>
                    <div
                      title={
                        asset.oppScore != null
                          ? `Opportunity Score: ${(Number(asset.oppScore) / 10).toFixed(1)}/10 (Grade ${asset.grade || "N/A"})`
                          : "Opportunity score not available"
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "7px",
                        padding: "4px 8px",
                        borderRadius: "7px",
                        background: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.03)",
                        border: isDark ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid rgba(0, 0, 0, 0.06)"
                      }}
                    >
                      {/* Grade Pill */}
                      <div
                        style={{
                          padding: "2px 7px",
                          borderRadius: "5px",
                          background: `${gradeColor}18`,
                          border: `1px solid ${gradeColor}44`,
                          fontSize: "11.5px",
                          fontWeight: 800,
                          color: gradeColor,
                          minWidth: "25px",
                          textAlign: "center"
                        }}
                      >
                        {asset.grade || "—"}
                      </div>

                      {/* Opp Score on 10 */}
                      <div style={{ textAlign: "right" }}>
                        <span
                          style={{
                            fontFamily: "'JetBrains Mono', Consolas, monospace",
                            fontSize: "12.5px",
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#0f172a"
                          }}
                        >
                          {asset.oppScore != null ? (Number(asset.oppScore) / 10).toFixed(1) : "—"}
                        </span>
                        {asset.oppScore != null && (
                          <span
                            style={{
                              fontSize: "9.5px",
                              color: isDark ? "#64748b" : "#94a3b8",
                              fontWeight: 500,
                              marginLeft: "1px"
                            }}
                          >
                            /10
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssetRankingsModal;
